import { getRoute } from "../core/getRoute";
import { setConfig, getConfig } from "../constants/config";
import { Route } from "./route";
import tokenService from "../../common/tokenService.js";
// Force rebuild trigger

export class Symphony {
  constructor({ walletClient = undefined, options = {} } = {}) {
    this.walletClient = walletClient;
    setConfig(options);
    this._warmTokenCache();
  }

  connectWalletClient(walletClient) {
    this.walletClient = walletClient;
  }
  disconnectWalletClient() {
    this.walletClient = undefined;
  }
  async getRoute(tokenIn, tokenOut, amountIn, options = {}) {
    const defaultOptions = { isRaw: false };
    const mergedOptions = { ...defaultOptions, ...options };
    const routePayload = await getRoute(
      tokenIn,
      tokenOut,
      amountIn,
      mergedOptions
    );

    // Pass a getter function to the Route object
    return new Route(routePayload, () => this.walletClient, mergedOptions);
  }

  /**
   * Gets the list of supported tokens (synchronous).
   * Uses static token list with user configuration applied.
   * @deprecated This method uses a static token list. Use `getTokenListAsync` for live data.
   * @returns {Object} Map of token addresses to token metadata
   */
  getTokenList() {
    const config = getConfig();
    // Use tokenService to get static token list with merging logic
    const tokens = tokenService.getStaticTokenList(
      config.chainId,
      config.additionalTokens || {},
      config.overrideDefaultTokens || false,
      config.nativeAddress
    );
    return { ...tokens };
  }

  /**
   * Fetches the token list asynchronously from external API
   * Uses caching to avoid repeated API calls within the same session
   * Falls back to static tokens automatically if API fetch fails
   * Respects additionalTokens and overrideDefaultTokens configuration
   *
   * @returns {Promise<Object>} Token list object with token addresses as keys
   *
   * @example
   * const symphony = new Symphony();
   * const tokens = await symphony.getTokenListAsync();
   * console.log(tokens); // { "0x0": { id: "...", attributes: {...} }, ... }
   */
  async getTokenListAsync() {
    const config = getConfig();
    const { chainId, additionalTokens, overrideDefaultTokens, nativeAddress } =
      config;

    const tokens = await tokenService.getAsyncTokenList(
      chainId,
      additionalTokens,
      overrideDefaultTokens,
      nativeAddress
    );
    return { ...tokens };
  }

  /**
   * Checks if a token is listed in the static token list (synchronous).
   * Uses static token list with user configuration applied.
   * @deprecated This method uses a static token list. Use `isTokenListedAsync` for live data.
   * @param {string} tokenAddress - Token address to check
   * @returns {boolean} True if token is listed, false otherwise
   */
  isTokenListed(tokenAddress) {
    if (typeof tokenAddress !== "string" || !tokenAddress.startsWith("0x"))
      console.error(
        `Cannot verify token 'tokenAddress' parameter is not valid. tokenAddress: ${tokenAddress}`
      );

    const config = getConfig();
    // Use tokenService to get static token list with merging logic
    const tokens = tokenService.getStaticTokenList(
      config.chainId,
      config.additionalTokens || {},
      config.overrideDefaultTokens || false,
      config.nativeAddress
    );

    const token = tokens[tokenAddress?.toString()?.toLowerCase()];
    return !!token;
  }

  /**
   * Checks if a token is listed in the async token list (asynchronous).
   * Uses live token data from API with fallback to static tokens.
   * Respects additionalTokens and overrideDefaultTokens configuration.
   *
   * @param {string} tokenAddress - Token address to check
   * @returns {Promise<boolean>} Promise that resolves to true if token is listed, false otherwise
   *
   * @example
   * const symphony = new Symphony();
   * const isListed = await symphony.isTokenListedAsync("0x1234...");
   * console.log(isListed); // true or false
   */
  async isTokenListedAsync(tokenAddress) {
    if (typeof tokenAddress !== "string" || !tokenAddress.startsWith("0x"))
      console.error(
        `Cannot verify token 'tokenAddress' parameter is not valid. tokenAddress: ${tokenAddress}`
      );

    const config = getConfig();
    const { chainId, additionalTokens, overrideDefaultTokens, nativeAddress } =
      config;

    // Get updated token list with user's configuration applied
    const tokens = await tokenService.getAsyncTokenList(
      chainId,
      additionalTokens,
      overrideDefaultTokens,
      nativeAddress
    );

    const token = tokens[tokenAddress?.toString()?.toLowerCase()];
    return !!token;
  }

  getConfig() {
    return getConfig();
  }

  setConfig(options) {
    setConfig(options);
  }

  /**
   * Proactively fetches token list to warm the cache during initialization.
   * This is a fire-and-forget operation that doesn't block the constructor.
   * @private
   */
  _warmTokenCache() {
    try {
      console.log("Symphony: Warming token cache...");
      const startTime = Date.now();

      const config = getConfig();
      const {
        chainId,
        additionalTokens,
        overrideDefaultTokens,
        nativeAddress,
      } = config;

      console.log("Symphony: Starting background token fetch to warm cache");

      // Start background fetch - don't await to avoid blocking constructor
      tokenService
        .getAsyncTokenList(
          chainId,
          additionalTokens,
          overrideDefaultTokens,
          nativeAddress
        )
        .then((tokens) => {
          const endTime = Date.now();
          console.log(
            `Symphony: Token cache warm-up complete. ${
              Object.keys(tokens).length
            } tokens loaded in ${endTime - startTime}ms`
          );
        })
        .catch((error) => {
          // Silently handle errors - fallback will work when user actually calls getTokenListAsync
          console.warn(
            "Symphony: Background token fetch failed, will fallback when needed:",
            error.message
          );
        });
    } catch (error) {
      // Silently handle any config errors - user will get proper error when calling getTokenListAsync
      console.warn(
        "Symphony: Could not start background token fetch:",
        error.message
      );
    }
  }
}
